import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { getArticleBySlug } from "@/mocks/articles";
import { mainSiteUrl } from "@/mocks/categories";
import Footer from "@/pages/home/components/Footer";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://itaobuy.yachts";
const siteLogo = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg";

function buildArticleSchema(article: NonNullable<ReturnType<typeof getArticleBySlug>>) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.imageUrl,
    "datePublished": "2026-06-28",
    "dateModified": "2026-06-28",
    "author": {
      "@type": "Organization",
      "name": "ItaobuySpreadsheet",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "ItaobuySpreadsheet",
      "logo": {
        "@type": "ImageObject",
        "url": siteLogo
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/guides/${article.slug}`
    }
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const effectiveSlug = slug || "what-is-itaobuy-sheet";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = effectiveSlug ? getArticleBySlug(effectiveSlug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-6">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
          <Link
            to="/"
            className="px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Back to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={buildArticleSchema(article) as Record<string, unknown>} />
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight whitespace-nowrap cursor-pointer"
            >
              Itaobuy
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="px-4 py-2 text-sm font-semibold text-foreground-700 hover:text-foreground-950 transition-colors rounded-lg whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                <i className="ri-home-4-line"></i>
                Home
              </Link>
              <a
                href="/#blog"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("blog")?.scrollIntoView({ behavior: "smooth" });
                  window.history.replaceState(null, "", "/#blog");
                }}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors rounded-lg whitespace-nowrap cursor-pointer"
              >
                Shopping Guide
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                itaobuy sheet
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <article className="max-w-3xl mx-auto px-6 py-12 md:py-20">
          <header className="mb-12">
            <p className="text-xs md:text-sm font-medium text-foreground-500 tracking-[0.15em] uppercase mb-4 whitespace-nowrap">
              {article.category}
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-[1.1] mb-6">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="mt-6 w-full h-56 md:h-72 rounded-2xl overflow-hidden bg-background-200">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </header>

          <div className="space-y-8">
            {article.content.map((block, i) => {
              if (block.type === "heading") {
                return (
                  <h2 key={i} className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10">
                    {block.value}
                  </h2>
                );
              }
              if (block.type === "paragraph") {
                return (
                  <p key={i} className="text-base text-foreground-700 leading-relaxed">
                    {block.value}
                  </p>
                );
              }
              if (block.type === "list" && block.items) {
                return (
                  <ul key={i} className="space-y-3">
                    {block.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-accent-500"></i>
                        </span>
                        <span className="text-sm text-foreground-700 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              if (block.type === "image") {
                return (
                  <div key={i} className="w-full h-48 md:h-64 rounded-xl overflow-hidden bg-background-200">
                    <img
                      src={block.value}
                      alt="Article illustration"
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>

          {article.faq && article.faq.length > 0 && (
            <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200/60">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-4">Frequently Asked Questions</h3>
              <div className="space-y-4">
                {article.faq.map((f, i) => (
                  <div key={i}>
                    <p className="text-sm font-semibold text-foreground-900 mb-1">{f.q}</p>
                    <p className="text-sm text-foreground-600 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 p-8 bg-accent-100/50 rounded-2xl border border-accent-200/50 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
              Ready to Start Sourcing Smarter?
            </h3>
            <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto leading-relaxed">
              Join thousands of dropshippers and sellers who have already switched to the itaobuy spreadsheet system.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-8 py-3.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Open Itaobuy Spreadsheet
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <Link
                to="/"
                className="px-8 py-3.5 text-sm font-semibold border-2 border-foreground-300 text-foreground-800 rounded-full hover:border-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Back to Homepage
              </Link>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between text-sm text-foreground-500">
            <Link to="/" className="hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line mr-1"></i>
              Return to Homepage
            </Link>
            <span className="whitespace-nowrap">{article.date}</span>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}